import { DateTime } from 'luxon';
import { DateMode, FormFieldChoice, FormFieldType, type FormField, type MatrixValue } from './form';
import { DATE_FORMAT_7, formattedDate, getUTCJSDate } from '@/utils';
import { getInitialMatrixData } from '@/components/data/entry/items/MatrixHelper';

export interface ISurgeonDashboard {
  perfusionSummaryData: FormField[];
    gases?: FormField[];
    timeSeriesTimepoints?: FormFieldChoice[];
    flows?: FormField[];
    labs?: FormField[];
    bile?: FormField[];
}

/**
 * What a summary field with nothing in it reads as.
 *
 * Written into `value` by every branch below and then filtered out of the array, so a variable the
 * case never recorded costs no tile at all rather than a tile with a label and a blank under it.
 * `SurgeonDashboard.vue` and `InteractiveDashboard.vue` compare against this exact string when they
 * look for the NMP start date, so it is one spelling in one place: the stray `'No Data'` the unit
 * branch used to write was not the string the filter tested for, and survived onto the screen.
 */
const NO_DATA = 'No data';

/**
 * Whether the backend sent anything for this field.
 *
 * `!value` is the wrong test on a reading of `0` — a bile volume, a flow that stopped, a lab that
 * read zero. The combined `perfusion-dashboard` route sends `value` with its JSON type intact
 * rather than stringified, so both the numeric `0` and the string `'0'` reach here.
 */
const hasValue = (value: unknown) =>
  value !== undefined && value !== null && value !== '';

/**
 * One summary field's `value` parsed as JSON, or `undefined` when it will not parse.
 *
 * Unguarded, this threw out of the `forEach` below and cost the whole payload: the summary, the
 * timepoints, and — because the sections are columned by those timepoints — all four sections. One
 * unreadable field is one missing field.
 */
const parseFieldValue = <T>(formField: FormField): T | undefined => {
  try {
    return JSON.parse(`${formField.value}`) as T;
  } catch (error) {
    // Not routed through the error store: nothing the user can act on, and a toast per bad field
    // would bury the readings they came for.
    // eslint-disable-next-line no-console
    console.error('[surgeon-dashboard] summary field value is not valid JSON', {
      variableName: formField.variableName,
      error
    });
    return undefined;
  }
};

export class SurgeonDashboardData {
  perfusionSummaryData: FormField[];
  timeSeriesTimepoints: FormFieldChoice[];
  gases: FormField[];
  flows: FormField[];
  labs: FormField[];
  bile: FormField[];

  constructor(data: ISurgeonDashboard) {
    this.perfusionSummaryData = data.perfusionSummaryData;
    this.gases = data.gases ?? [];
    this.timeSeriesTimepoints = data.timeSeriesTimepoints ?? [];
    this.flows = data.flows ?? [];
    this.labs = data.labs ?? [];
    this.bile = data.bile ?? [];
  }

  /**
   * Turn a summary payload into the strings the tiles render.
   *
   * Every read of the summary goes through here — the single-summary route, the interactive route
   * and the combined streamed one — because this is the only place that knows a date-time column
   * holds epoch seconds and a unit column holds `{"value":770,"unit":"g"}`. A path that publishes
   * the payload without this call puts those on screen verbatim.
   */
  static mapFromBackend(networkData: ISurgeonDashboard) {

    if (Object.keys(networkData).length == 0) {
      return undefined;
    }

    networkData.perfusionSummaryData?.forEach(formField => {
      const value = formField.value;

      switch (formField.type) {
        case FormFieldType.CHOICE:
        case FormFieldType.DROPDOWN:
          if (formField.options.maximumChoicesCount == 1) {
            // A code with no matching choice used to leave `value` undefined, which the filter
            // below let through — that is a labelled tile with nothing under it.
            formField.value = (hasValue(value) ?
              formField.options.choices?.find(choice => choice.value == value)?.label :
              undefined) ?? NO_DATA;
          } else {
            const chosen = hasValue(value) ? `${value}`.split(',') : [];
            const labels = chosen
              .map(choiceValue =>
                formField.options.choices?.find(choice => choice.value == choiceValue)?.label
              )
              .filter((label): label is string => label !== undefined);
            // `join` on nothing is `''`, which the filter below also let through.
            formField.value = labels.length > 0 ? labels.join(',') : NO_DATA;
          }
          break;

        case FormFieldType.DATE: {
          // `Number` rather than `parseInt`: the streamed route sends the seconds as a number and
          // the per-section route as a string. A value that is neither has to be caught here —
          // `formattedDate` on an invalid date wrote "Invalid DateTime" into the tile.
          const seconds = hasValue(value) ? Number(value) : NaN;
          if (Number.isFinite(seconds)) {
            if (formField.options.mode === DateMode.DATE) {
              formField.dateValue = getUTCJSDate(seconds);
            } else if (formField.options.mode === DateMode.TIME) {
              formField.dateValue = DateTime.now().startOf('day').plus({ seconds }).toJSDate();
            } else if (formField.options.absoluteDate) {
              formField.dateValue = getUTCJSDate(seconds);
            } else {
              // Anything else is read as a date-time, an unset `mode` included: it is the widest of
              // the three, and the branch that used to fall through here set no `dateValue` at all.
              formField.dateValue = DateTime.fromSeconds(seconds).toJSDate();
            }
            formField.value = formattedDate(formField.dateValue, DATE_FORMAT_7);
          } else {
            formField.value = NO_DATA;
          }
          break;
        }

        case FormFieldType.UNIT: {
          const unitValue = hasValue(value) ?
            parseFieldValue<{ value?: number | string; unit?: string }>(formField) :
            undefined;
          const amount = hasValue(unitValue?.value) ? Number(unitValue!.value) : NaN;
          if (Number.isFinite(amount)) {
            formField.numberValue = amount;
            // The unit is half the reading, but a field that carries no unit must not print one:
            // `${amount} ${undefined}` reads as "770 undefined".
            formField.value = unitValue!.unit ?
              `${amount.toFixed(2)} ${unitValue!.unit}` :
              amount.toFixed(2);
          } else {
            formField.value = NO_DATA;
          }
          break;
        }

        case FormFieldType.NUMBER: {
          const amount = hasValue(value) ? Number(value) : NaN;
          formField.value = Number.isFinite(amount) ? amount.toFixed(2) : NO_DATA;
          break;
        }

        case FormFieldType.FILE:
          if (hasValue(value)) {
            //@ts-expect-error hasTimePoints is for dashboard variables
            if (formField.hasTimePoints) {
              formField.images = parseFieldValue<{ label: string, value: string }[]>(formField) ?? [];
            } else {
              formField.fileIds = parseFieldValue<string[]>(formField) ?? [];
            }
          }
          break;

        case FormFieldType.MATRIX:
          formField.matrixValues = hasValue(value) ?
            parseFieldValue<MatrixValue[]>(formField) ?? [] :
            [];
          formField.matrixTableRowValues = getInitialMatrixData(formField);
          break;

        default:
          // Stringified, because the streamed route's values keep their JSON type and the cell that
          // renders this declares a `String` prop — a raw number failed Vue's type check.
          formField.value = hasValue(value) ? `${value}` : NO_DATA;
          break;
      }
    });

    // FILE and MATRIX carry their content in `fileIds`/`images` and `matrixValues` rather than in
    // `value`, and the store splits both out of this array before anything renders; judging them by
    // `value` here would drop an attached image or a populated table.
    const summaryData = networkData.perfusionSummaryData?.filter(field =>
      field.type === FormFieldType.FILE ||
      field.type === FormFieldType.MATRIX ||
      field.value !== NO_DATA
    ) ?? [];

    return new SurgeonDashboardData({
      perfusionSummaryData: summaryData,
      gases: networkData.gases,
      timeSeriesTimepoints: networkData.timeSeriesTimepoints,
      flows: networkData.flows,
      labs: networkData.labs,
      bile: networkData.bile
    });
  }
}

// eslint-disable-next-line no-shadow
export enum SurgeonDashboardImageArrayType {
  LIVER_IMAGES,
  BIOPSY_IMAGES
}

export interface GraphGroup {
  label: string;
  expanded: boolean;
  data?: FormField[];
}

export interface DashboardCohortFilter {
  label: string;
  value: string;
  selected: boolean;
  key: string;
}

export interface DashboardCohortFilterGroup {
  id: number;
  label: string;
  filters: DashboardCohortFilter[];
}
